import { Fragment, useState } from "react"

import {
  useFailedCampaignJobs,
  type FailedCampaignJobRow,
} from "@/api/hooks/campaigns"

function fmtDate(s: string | null | undefined): string {
  if (!s) return "—"
  return new Date(s).toLocaleString()
}

function DiagField({
  label,
  value,
  mono,
}: {
  label: string
  value: string | number | boolean | null | undefined
  mono?: boolean
}) {
  const display =
    value === null || value === undefined ? "—" : String(value)
  return (
    <div>
      <p className="text-white/50 text-[11px]">{label}</p>
      <p className={`text-white text-xs ${mono ? "font-mono" : ""}`}>
        {display}
      </p>
    </div>
  )
}

function ExpandedRow({ job }: { job: FailedCampaignJobRow }) {
  return (
    <tr className="bg-[#2d2d2d]">
      <td colSpan={5} className="px-4 py-3">
        <div className="grid gap-x-6 gap-y-3 text-xs sm:grid-cols-3">
          <DiagField label="Job ID" value={job.job_id} mono />
          <DiagField label="Advertiser ID" value={job.advertiser_id} mono />
          <DiagField label="User" value={job.user_name} />
          <DiagField label="Objective" value={job.objective_type} />
          <DiagField label="Creative mode" value={job.creative_mode} />
          <DiagField label="Budget" value={job.budget} />
          <DiagField label="Locations" value={job.locations?.join(", ")} />
          <DiagField
            label="Smart+"
            value={
              job.is_smart_plus === true
                ? "Yes"
                : job.is_smart_plus === false
                  ? "No"
                  : null
            }
          />
          <DiagField label="Attempts" value={job.attempt_count} />
          <DiagField label="Created" value={fmtDate(job.created_at)} />
          <DiagField label="Started" value={fmtDate(job.started_at)} />
          <DiagField label="Failed at" value={fmtDate(job.completed_at)} />
        </div>
        <div className="mt-3">
          <p className="text-white/50 text-[11px] mb-1">Error message</p>
          <pre className="max-h-48 overflow-auto rounded border border-[#4a4a4a] bg-[#1f1f1f] p-2 text-[11px] text-red-300 whitespace-pre-wrap font-mono">
            {job.error_message ?? "—"}
          </pre>
        </div>
      </td>
    </tr>
  )
}

export default function CampaignJobsTab() {
  const {
    data: failedData,
    isLoading,
    error,
    refetch,
  } = useFailedCampaignJobs(50)
  const jobs = failedData?.data ?? []
  const [expandedId, setExpandedId] = useState<string | null>(null)

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="border-b-2 border-[#8b9aff] pb-2 text-2xl font-semibold text-white">
            Failed Campaign Jobs
          </h2>
          <p className="mt-1 text-sm text-white/70">
            Click a row to see full diagnostic details.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <span className="text-xs text-white">
            {isLoading
              ? "Loading..."
              : error
                ? "Error loading jobs"
                : `${jobs.length} failed jobs`}
          </span>
          <button
            onClick={() => refetch()}
            className="rounded-md border border-[#676d78] bg-[#4f5561] px-3 py-1 text-xs font-semibold text-white shadow-sm transition-colors hover:bg-[#5a616f]"
          >
            Refresh
          </button>
        </div>
      </div>

      {error ? (
        <pre className="overflow-auto rounded border border-red-800 bg-[#2a1515] p-3 text-[11px] text-red-300 whitespace-pre-wrap font-mono">
          {error instanceof Error ? error.message : String(error)}
        </pre>
      ) : null}

      <div className="overflow-x-auto rounded-lg border border-[#4a4a4a]">
        <table className="w-full text-left text-sm">
          <thead className="bg-[#444444] text-white">
            <tr>
              <th className="px-4 py-3 font-medium">Failed at</th>
              <th className="px-4 py-3 font-medium">Campaign</th>
              <th className="px-4 py-3 font-medium">User</th>
              <th className="px-4 py-3 font-medium">Advertiser</th>
              <th className="px-4 py-3 font-medium">Error</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-[#4a4a4a]">
            {isLoading ? (
              <tr>
                <td colSpan={5} className="px-4 py-8 text-center text-white">
                  Loading...
                </td>
              </tr>
            ) : error ? (
              <tr>
                <td
                  colSpan={5}
                  className="px-4 py-8 text-center text-red-400"
                >
                  Failed to load jobs.
                  <div className="mt-2 text-[11px] font-mono text-red-300/80">
                    {error instanceof Error ? error.message : String(error)}
                  </div>
                </td>
              </tr>
            ) : jobs.length === 0 ? (
              <tr>
                <td colSpan={5} className="px-4 py-8 text-center text-white">
                  No failed jobs found.
                </td>
              </tr>
            ) : (
              jobs.map((job) => {
                const isOpen = expandedId === job.job_id
                return (
                  <Fragment key={job.job_id}>
                    <tr
                      onClick={() =>
                        setExpandedId(isOpen ? null : job.job_id)
                      }
                      className="cursor-pointer transition-colors hover:bg-[#444444]"
                    >
                      <td className="whitespace-nowrap px-4 py-3 text-xs text-white">
                        {fmtDate(job.completed_at || job.created_at)}
                      </td>
                      <td className="px-4 py-3 text-xs text-white">
                        {job.campaign_name}
                      </td>
                      <td className="px-4 py-3 text-xs text-white/80">
                        {job.user_name ?? "—"}
                      </td>
                      <td className="px-4 py-3 text-xs text-white/80 font-mono">
                        {job.advertiser_id}
                      </td>
                      <td className="max-w-[400px] px-4 py-3">
                        <p className="truncate text-xs text-red-300">
                          {job.error_message ?? "—"}
                        </p>
                      </td>
                    </tr>
                    {isOpen && <ExpandedRow job={job} />}
                  </Fragment>
                )
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  )
}
